// Kept as the single product-configuration module so existing imports remain
// stable. Civil/criminal and Ontario/BC are matter attributes, not editions.
export const EDITION = 'unified';

export const editionConfig = {
  id: 'unified',
  productName: 'Lex Maple',
  platformName: 'Lex Maple',
  shortName: 'NEO workspace',
  practiceLabel: 'Ontario paralegal practice',
  researchLabel: 'Canadian authorities',
  defaultStage: 'intake-scope',
  practiceArea: 'Ontario paralegal matter',
};

export const isNeoLegalFirm = name => /\bneo\s+legal\b/i.test(String(name || ''));

export function firmDisplayName(name) {
  const value = String(name || '').trim();
  if (!value) return 'Your firm';
  if (isNeoLegalFirm(value)) return 'NEO Legal';
  return value
    .replace(/\s+Professional Corporation$/i, '')
    .replace(/\s+Law Corporation$/i, '')
    .trim();
}

export const firmProfile = {
  legalName: 'NEO Legal Services Professional Corporation',
  displayName: 'NEO Legal',
  brandPromise: 'Built on Law. Driven by Judgement.',
  positioning: 'Practical, litigation-ready legal services for Ontario individuals and small businesses.',
  mission: 'Accessible, disciplined and results-oriented legal services that help Ontario clients resolve disputes, recover money and protect their legal position within licensed paralegal scope.',
  principal: 'Mo. Farooqui',
  professionalTitle: 'Principal · Licensed Paralegal',
  licence: 'LSO P21785',
  jurisdiction: 'Ontario',
  serviceModel: 'Virtual-first · province-wide · in-person by appointment',
  founded: 'February 2026',
  address: '1761 Magenta Road, Pickering, Ontario L1X 0E1',
  phone: '647-200-3526',
  tollFree: '888-519-2435',
  email: 'Ceo@neolegalservices.com',
  website: 'https://www.neolegalservices.com',
  practiceAreas: [
    'Small Claims & civil disputes',
    'Commercial collections',
    'Employment & contractor matters',
    'Landlord & Tenant Board',
    'Provincial offences',
    'Construction payment disputes',
    'Human rights & benefits appeals',
    'Notary & document services',
  ],
  clientSegments: ['Individuals', 'Contractors & trades', 'Landlords & tenants', 'Small businesses', 'Portfolio creditors'],
  serviceStandards: [
    'Acknowledge new inquiries within one business day',
    'Run conflicts and scope triage before substantive advice',
    'Escalate urgent deadlines immediately',
    'Record authority before any settlement or material concession',
    'Issue a closing report with outcome, money, deadlines and next steps',
  ],
  operatingPrinciples: ['Integrity', 'Clarity', 'Judgement', 'Urgency', 'Documentation', 'Respect', 'Accountability'],
};

// NEO's internal service catalogue, transcribed from the October 2026 business
// plan. These are firm defaults for quoting and budgeting, not automatic fees.
export const neoFirmRates = [
  { service: 'Hourly work', fee: '$175 / hour', detail: 'Plus HST; use for unpredictable or continuing work.' },
  { service: 'Landlord & Tenant Board', fee: '$1,000-$1,500', detail: 'Per application; $750 per hearing day.' },
  { service: 'Small Claims Court', fee: '$300-$3,500', detail: 'Per defined stage.' },
  { service: 'Traffic & provincial offences', fee: '$250 / $1,000+', detail: 'Part I-II flat fee / Part III matters.' },
  { service: 'Immigration (authorized representative)', fee: '$1,000-$6,000', detail: 'Only where separately authorized and within scope.' },
  { service: 'Notary & commissioning', fee: '$35 + $20/page', detail: 'First page, then each additional page; extras quoted separately.' },
  { service: 'Starter subscription', fee: '$199 / month', detail: 'One short consultation, limited document review, standard response.' },
  { service: 'Business subscription', fee: '$499 / month', detail: 'Up to two consultations plus one demand or contract review.' },
  { service: 'Growth subscription', fee: '$999 / month', detail: 'Defined monthly hours, quarterly risk review, priority service.' },
];

export function matterTypeFor(value = {}) {
  const explicit = String(value.matterType || value.practiceTrack || value.intake?.matterType || '').toLowerCase();
  if (/criminal|offence|regulatory/.test(explicit)) return 'criminal';
  if (/civil|claim|dispute/.test(explicit)) return 'civil';
  if (Array.isArray(value.intake?.charges) && value.intake.charges.length) return 'criminal';
  if (/^r\.?\s+v\.?\s+/i.test(String(value.title || value.matterName || ''))) return 'criminal';
  return /criminal|offence/.test(String(value.practiceArea || '').toLowerCase()) ? 'criminal' : 'civil';
}

export function matterLanguage(value = {}) {
  const criminal = matterTypeFor(value) === 'criminal';
  return criminal ? {
    type: 'criminal',
    label: 'Criminal / regulatory',
    evidence: 'disclosure and evidence',
    issues: 'charges / allegations',
  } : {
    type: 'civil',
    label: 'Dispute / claim',
    evidence: 'documents and evidence',
    issues: 'claims / issues',
  };
}

export const storageKey = suffix => `lexmaple.workspace.${suffix}`;
export const ACTIVE_MATTER_KEY = storageKey('activeMatterId');
export const COPILOT_PROFILE_KEY = storageKey('copilot.profile.v1');
export const COPILOT_PROGRESS_KEY = storageKey('copilot.progress.v1');
export const COPILOT_PREFILL_KEY = storageKey('copilot.prefill');
export const USER_SESSION_KEY = storageKey('user');
